import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCart, addToCart, updateCartItem, clearCart } from '@/lib/api';
import { toast } from 'sonner';

const CartContext = createContext(null);

export const CartProvider = ({ children, storeId }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!storeId) return;
    try {
      const data = await getCart(storeId);
      setCart(data || { items: [], subtotal: 0 });
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }, [storeId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addItem = async (item) => {
    setLoading(true);
    try {
      const result = await addToCart(storeId, item);
      if (result.cart) {
        setCart(result.cart);
      }
      toast.success('Producto agregado al carrito');
      return true;
    } catch (error) {
      toast.error(error.message || 'Error al agregar al carrito');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (item) => {
    setLoading(true);
    try {
      const result = await updateCartItem(storeId, item);
      if (result.cart) {
        setCart(result.cart);
      }
      return true;
    } catch (error) {
      toast.error(error.message || 'Error al actualizar carrito');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (productId, variantId, variantOptions = null) => {
    return updateItem({ 
      product_id: productId, 
      variant_id: variantId, 
      variant_options: variantOptions,
      quantity: 0 
    });
  };

  const emptyCart = async () => {
    try {
      await clearCart(storeId);
      setCart({ items: [], subtotal: 0 });
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const itemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const value = {
    cart,
    loading,
    isOpen,
    setIsOpen,
    addItem,
    updateItem,
    removeItem,
    emptyCart,
    fetchCart,
    itemCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
